import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconDownload, IconEdit, IconTrash } from '../components/icons';
import { Modal } from '../components/Modal';
import Pagination from '../components/Pagination';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  category: string;
  dateFrom: string;
  dateTo: string;
}

const emptyFilters: Filters = { search: '', category: '', dateFrom: '', dateTo: '' };

interface DocumentRow {
  id: number;
  user_id: number;
  user_name: string;
  employee_code: string | null;
  category: string;
  original_name: string;
  notes: string | null;
  date_on_file: string | null;
}

interface UserOption {
  id: number;
  full_name: string;
  [key: string]: unknown;
}

const emptyAddForm = { user_id: '' as string | number, category: '', notes: '', date_on_file: '' };

export function Documents() {
  const { can } = useAuth();
  const { lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();

  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [addFile, setAddFile] = useState<File | null>(null);
  const [editing, setEditing] = useState<DocumentRow | null>(null);
  const [editForm, setEditForm] = useState({ category: '', notes: '', date_on_file: '' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const canCreate = can('hr.create');
  const canUpdate = can('hr.update');
  const canArchive = can('hr.archive');

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      category: filters.category || undefined,
      date_from: filters.dateFrom || undefined,
      date_to: filters.dateTo || undefined,
    }),
    [filters]
  );

  const { data, loading, error, setPage, reload } = usePaginatedList<DocumentRow>('/employee-documents', query);

  const openAdd = () => {
    setAddForm(emptyAddForm);
    setAddFile(null);
    setErrors({});
    setAdding(true);
  };

  const onAddFileSelected = (e: ChangeEvent<HTMLInputElement>) => {
    setAddFile(e.target.files?.[0] ?? null);
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!addFile) return;
    const data = new FormData();
    data.append('user_id', String(addForm.user_id));
    data.append('category', addForm.category);
    if (addForm.notes) data.append('notes', addForm.notes);
    if (addForm.date_on_file) data.append('date_on_file', addForm.date_on_file);
    data.append('file', addFile);
    try {
      await api.post('/employee-documents', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAdding(false);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const openEdit = (row: DocumentRow) => {
    setEditing(row);
    setEditForm({ category: row.category, notes: row.notes ?? '', date_on_file: row.date_on_file ?? '' });
    setErrors({});
  };

  const submitEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/employee-documents/${editing.id}`, editForm);
      setEditing(null);
      reload();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (row: DocumentRow) => {
    if (!(await confirmAction(`Delete this document for ${row.user_name}?`))) return;
    try {
      await api.delete(`/employee-documents/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const download = (row: DocumentRow) => downloadFile(api, `/employee-documents/${row.id}/download`, row.original_name);

  return (
    <>
      <header className="page-header">
        <h1>Documents</h1>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={openAdd}>
            + Add
          </button>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All types</option>
          {listOptions(lists, 'employee_document_type').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <label className="checkbox-inline">
          Date from
          <input type="date" value={filters.dateFrom} onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })} />
        </label>
        <label className="checkbox-inline">
          Date to
          <input type="date" value={filters.dateTo} onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })} />
        </label>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Employee Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>File</th>
                <th>Date on File</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.id}>
                    <td className="muted">{row.employee_code ?? '—'}</td>
                    <td>
                      <strong>{row.user_name}</strong>
                    </td>
                    <td>{labelFor(lists, 'employee_document_type', row.category)}</td>
                    <td>
                      <IconButton icon={IconDownload} label={row.original_name} onClick={() => download(row)} />
                    </td>
                    <td className="muted">{row.date_on_file ?? '—'}</td>
                    <td className="right">
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(row)} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="muted center">
                    No documents yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>

      {adding && (
        <Modal title="Add document" onClose={() => setAdding(false)}>
          <form onSubmit={submitAdd} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <SearchableSelect<UserOption>
                endpoint="/users"
                value={addForm.user_id}
                onChange={(id) => setAddForm({ ...addForm, user_id: id })}
                getLabel={(u) => u.full_name}
                placeholder="Search staff…"
              />
              {errors.user_id && <small className="error">{errors.user_id[0]}</small>}
            </label>
            <label className="span-2">
              Type *
              <select value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} required>
                <option value="">Select…</option>
                {listOptions(lists, 'employee_document_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.category && <small className="error">{errors.category[0]}</small>}
            </label>
            <label className="span-2">
              File *
              <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={onAddFileSelected} required />
              {errors.file && <small className="error">{errors.file[0]}</small>}
            </label>
            <label className="span-2">
              Date on File
              <input
                type="date"
                value={addForm.date_on_file}
                onChange={(e) => setAddForm({ ...addForm, date_on_file: e.target.value })}
              />
              {errors.date_on_file && <small className="error">{errors.date_on_file[0]}</small>}
            </label>
            <label className="span-2">
              Notes
              <textarea rows={3} value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setAdding(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!addForm.user_id || !addForm.category || !addFile}>
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit document for ${editing.user_name}`} onClose={() => setEditing(null)}>
          <form onSubmit={submitEdit} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Type *
              <select
                value={editForm.category}
                onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                required
              >
                {listOptions(lists, 'employee_document_type').map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.category && <small className="error">{errors.category[0]}</small>}
            </label>
            <label className="span-2">
              Date on File
              <input
                type="date"
                value={editForm.date_on_file}
                onChange={(e) => setEditForm({ ...editForm, date_on_file: e.target.value })}
              />
              {errors.date_on_file && <small className="error">{errors.date_on_file[0]}</small>}
            </label>
            <label className="span-2">
              Notes
              <textarea
                rows={3}
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
